import Platform from "react-native-web/dist/exports/Platform";
/**
 * @typedef {import('react-native').AccessibilityRole} AccessibilityRole
 */
/**
 * This is based on the role-to-tag mapping that React Native Web uses to set HTML tags.
 * It's not exported in any way from RNW, so we need to keep this up-to-date manually.
 * https://github.com/necolas/react-native-web/blob/master/packages/react-native-web/src/modules/AccessibilityUtil/propsToAccessibilityComponent.js
 *
 * Note: every role in this list is a web-only aria-role. There is no overlap between _these_ web tags
 * or roles and native accessibilityRoles. Only h1, h2, h3 etc map to an RN equivalent ("heading").
 *
 * - RN "summary" native role maps to "region" aria-role, but setting `<section>`/"region" does not
 *   set React Native's "summary" role, which has a much narrower use case.
 * - `<Header>`/"Banner" also do not map to RN's "heading". Only h1 / h2 etc map to RN "heading".
 *
 * Therefore, all of these tags / roles default to no accessibilityRole in native apps. This is not wrong:
 * in general, RN accessibilityRoles tend to be more about interaction and less about semantics than web roles.
 *
 * RNW's one-way mapping of React Native accessibilityRoles to web aria-roles:
 * https://github.com/necolas/react-native-web/blob/master/packages/react-native-web/src/modules/AccessibilityUtil/propsToAriaRole.js
 */
// Adding `/** @type {const} */ ({...})` denotes object content as `readonly` in many IDEs
const rolesToTags = /** @type {const} */{
  article: 'article',
  banner: 'header',
  blockquote: 'blockquote',
  code: 'code',
  complementary: 'aside',
  contentinfo: 'footer',
  deletion: 'del',
  emphasis: 'em',
  figure: 'figure',
  insertion: 'ins',
  form: 'form',
  list: 'ul',
  listitem: 'li',
  main: 'main',
  navigation: 'nav',
  region: 'section',
  strong: 'strong',
  // Add special cases that are in RNW's function logic but not in its mapping object
  label: 'label'
};

// Invert React Native Web's mapping, so a tag gets the role that gets that tag
export const tagsToRoles = Object.fromEntries(Object.entries(rolesToTags).map(_ref => {
  let [key, value] = _ref;
  return [value, key];
}));

/**
 * Heading HTML tags map to the "heading" accessibilityRole in native apps, which is similar
 * to headings on web but without the expectation of a hierarchy of headings within one screen.
 */
export const headingTags = /** @type {const} */['h1', 'h2', 'h3', 'h4', 'h5', 'h6'];
/**
 * @typedef {typeof headingTags[number]} HeadingTag
 */

/**
 * All HTML tags that may be set via RNW accesibility props alone and therefore may be set using
 * the getA11yPropsFromHtmlTag function without changing other behaviour.
 *
 * Of these, only headers (h1, h2, ...h5, h6) set a corresponding accessibilityRole in native apps ("heading").
 */
export const supportedTags = [...Object.keys(tagsToRoles), ...headingTags];
/**
 * Uses readonly mapping keys/values to generate static types for IDEs that support TS in JSDoc.
 * @typedef {keyof rolesToTags} RoleWithTag
 * @typedef {typeof rolesToTags[RoleWithTag] | typeof headingTags[number]} SupportedTag
 */

/**
 * Subset of supported HTML tags that may be used with layout containers like Box.
 *
 * Of these, only headers (h1, h2, ...h5, h6) set a corresponding accessibilityRole in native apps ("heading").
 */
export const layoutTags = /** @type {const} */[...headingTags, 'article', 'aside', 'blockquote', 'footer', 'figure', 'form', 'header', 'ul', 'li', 'main', 'nav', 'section', 'label'];
/**
 * @typedef {typeof layoutTags[number]} LayoutTag
 */

/**
 * Subset of supported HTML tags that may be used with text elements like Typography.
 *
 * Of these, only headers (h1, h2, ...h5, h6) set a corresponding accessibilityRole in native apps ("heading").
 */
export const textTags = /** @type {const} */[...headingTags, 'blockquote', 'code', 'del', 'em', 'ins', 'li', 'strong', 'label'];
/**
 * @typedef {typeof layoutTags[number]} TextTag
 */

/**
 * If passed a heading tag string like 'h1', 'h2' etc, returns the heading number as a number
 * ready for use in `accessibilityLevel` props and similar.
 *
 * @param {string} [tag] - HTML tag string; returns undefined if not a {@link HeadingTag}
 * @returns {'1' | '2' | '3' | '4' | '5' | '6' | undefined}
 */
export const getHeadingLevel = tag => headingTags.includes(tag) ? Number(tag[1]) : undefined;

/**
 * Takes a supported HTML tag, and returns the accessibility props that, on web, make React Native Web
 * render that tag.
 *
 * For cross-platform apps, a second argument may be passed with an [accessibilityRole](https://reactnative.dev/docs/accessibility#accessibilityrole)
 * to use in native apps. Heading tags (h1, h2, ...h5, h6) map to "heading" role by default;
 * no other supported semantic HTML tags have an equivalent native accessibilityRole.
 *
 * @param {SupportedTag} tag - HTML tag to use on web
 * @param {AccessibilityRole | null} [nativeRole] - optional accessibilityRole for native apps
 * @returns {{ accessibilityRole: string, accessibilityLevel?: string } | undefined}
 */
export const getA11yPropsFromHtmlTag = (tag, nativeRole) => {
  // Allow cross-platform apps to set accessibilityRoles alongside a web tag without conflict
  if (nativeRole !== undefined && Platform.OS !== 'web') return {
    accessibilityRole: nativeRole
  };
  if (tag) {
    // aria roles don't have native counterparts and RN may throw an error if an unrecognised role is passed
    const ariaRole = Platform.OS === 'web' && tagsToRoles[tag];
    if (ariaRole) return {
      accessibilityRole: ariaRole
    };
    const accessibilityLevel = getHeadingLevel(tag);
    if (accessibilityLevel) return {
      accessibilityRole: 'header',
      accessibilityLevel
    };
  }
  // If nothing matches or no tag supplied, return undefined and let component decide how to fall back.
  // Note that return value may always be spread in objects (it is safe to spread undefined like { ...undefined })
  return undefined;
};